import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { createResponse, type BodyType } from '@/api/responses'
import { createScenario } from '@/api/scenarios'
import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { RuleBuilder, toApiRule, defaultGroupNode, type RuleNode } from './RuleBuilder'

// ── Types ──────────────────────────────────────────────────────────────────

const BODY_TYPES: BodyType[] = ['json', 'text', 'xml', 'binary_base64']

export interface ResponseData {
  status: number
  body_type: BodyType
  body: string
  headersJson: string
  delay_ms: number
}

export interface RuleEntry {
  id: string
  ruleNode: RuleNode
  response: ResponseData
}

// ── Schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  scenarioName: z.string().min(1).max(100),
  priority: z.coerce.number().int().default(0),
  enabled: z.boolean().default(true),
})

type FormValues = z.infer<typeof schema>

// ── Helpers ────────────────────────────────────────────────────────────────

export function defaultResponse(): ResponseData {
  return { status: 200, body_type: 'json', body: '{\n  \n}', headersJson: '{}', delay_ms: 0 }
}

export function defaultEntry(): RuleEntry {
  return { id: crypto.randomUUID(), ruleNode: defaultGroupNode('all'), response: defaultResponse() }
}

function parseHeaders(json: string): Record<string, string> {
  return JSON.parse(json) as Record<string, string>
}

// ── インライン レスポンスフォーム ────────────────────────────────────────────

export interface ResponseFormProps {
  value: ResponseData
  onChange: (v: ResponseData) => void
}

export function ResponseForm({ value, onChange }: ResponseFormProps) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2 rounded-md bg-muted/30 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.response')}</p>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">{t('scenarioForm.statusCode')}</Label>
          <Input
            type="number"
            className="h-8"
            value={value.status}
            onChange={e => onChange({ ...value, status: Number(e.target.value) })}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('scenarioForm.bodyType')}</Label>
          <Select value={value.body_type} onValueChange={v => onChange({ ...value, body_type: v as BodyType })}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BODY_TYPES.map(bt => <SelectItem key={bt} value={bt}>{bt}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t('scenarioForm.delayMs')}</Label>
          <Input
            type="number"
            className="h-8"
            value={value.delay_ms}
            onChange={e => onChange({ ...value, delay_ms: Number(e.target.value) })}
          />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t('scenarioForm.body')}</Label>
        <Textarea
          rows={3}
          className="font-mono text-sm"
          value={value.body}
          onChange={e => onChange({ ...value, body: e.target.value })}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t('scenarioForm.headers')}</Label>
        <Textarea
          rows={2}
          className="font-mono text-sm"
          placeholder="{}"
          value={value.headersJson}
          onChange={e => onChange({ ...value, headersJson: e.target.value })}
        />
      </div>
    </div>
  )
}

// ── Component ──────────────────────────────────────────────────────────────

interface ConditionalScenarioFormProps {
  projectId: string
  endpointId: string
  onSuccess: () => void
  onCancel: () => void
}

export function ConditionalScenarioForm({ projectId, endpointId, onSuccess, onCancel }: ConditionalScenarioFormProps) {
  const [serverError, setServerError] = useState<string | null>(null)
  const [ruleEntries, setRuleEntries] = useState<RuleEntry[]>([defaultEntry()])
  const [defaultResp, setDefaultResp] = useState<ResponseData | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { register, handleSubmit, control, formState: { errors, isSubmitting } } =
    useForm<FormValues>({
      resolver: zodResolver(schema),
      defaultValues: { scenarioName: '', priority: 0, enabled: true },
    })

  // ── エントリ操作 ──
  const updateEntry = (id: string, partial: Partial<RuleEntry>) => {
    setRuleEntries(prev => prev.map(e => e.id === id ? { ...e, ...partial } : e))
  }

  const removeEntry = (id: string) => {
    setRuleEntries(prev => prev.filter(e => e.id !== id))
  }

  const moveEntry = (id: string, dir: 'up' | 'down') => {
    setRuleEntries(prev => {
      const idx = prev.findIndex(e => e.id === id)
      if (idx < 0) return prev
      const next = [...prev]
      const swap = dir === 'up' ? idx - 1 : idx + 1
      if (swap < 0 || swap >= next.length) return prev
      ;[next[idx], next[swap]] = [next[swap], next[idx]]
      return next
    })
  }

  // ── Submit ──
  const mutation = useMutation({
    mutationFn: async (data: FormValues) => {
      if (ruleEntries.length === 0) throw new Error(t('scenarioForm.atLeastOneAction'))

      for (const entry of ruleEntries) {
        try { parseHeaders(entry.response.headersJson) } catch {
          throw new Error(t('scenarioForm.headersJsonError'))
        }
      }
      if (defaultResp) {
        try { parseHeaders(defaultResp.headersJson) } catch {
          throw new Error(t('scenarioForm.headersJsonError'))
        }
      }

      // ルールごとのレスポンスを並列作成
      const ruleResponses = await Promise.all(
        ruleEntries.map(e => createResponse(projectId, {
          status: e.response.status,
          body_type: e.response.body_type,
          body: e.response.body || undefined,
          headers: parseHeaders(e.response.headersJson),
          delay_ms: e.response.delay_ms,
        }))
      )

      // デフォルトレスポンス作成
      let defaultResponseId: string | undefined
      if (defaultResp) {
        const dr = await createResponse(projectId, {
          status: defaultResp.status,
          body_type: defaultResp.body_type,
          body: defaultResp.body || undefined,
          headers: parseHeaders(defaultResp.headersJson),
          delay_ms: defaultResp.delay_ms,
        })
        defaultResponseId = dr.id
      }

      const rules = ruleEntries.map((e, i) => ({
        if: toApiRule(e.ruleNode),
        then: { response_id: ruleResponses[i].id },
      }))

      await createScenario(endpointId, {
        name: data.scenarioName,
        priority: data.priority,
        enabled: data.enabled,
        type: 'conditional',
        config: {
          rules,
          ...(defaultResponseId ? { default: { response_id: defaultResponseId } } : {}),
        },
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['scenarios', endpointId] })
      onSuccess()
    },
    onError: (err) => {
      setServerError(
        err instanceof ApiError ? err.message :
        err instanceof Error ? err.message :
        t('scenarioForm.createFailed')
      )
    },
  })

  return (
    <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-5">
      {serverError && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{serverError}</p>
      )}

      {/* ── Scenario settings ──────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('scenarioForm.scenarioSettings')}</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="scenarioName">{t('scenarioForm.scenarioName')}</Label>
            <Input id="scenarioName" placeholder={t('scenarioForm.scenarioNamePlaceholder')} {...register('scenarioName')} />
            {errors.scenarioName && (
              <p className="text-sm text-destructive">{t('scenarioForm.nameRequired')}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="priority">{t('scenarioForm.priority')}</Label>
            <Input id="priority" type="number" {...register('priority')} />
          </div>
          <div className="space-y-1.5">
            <Label className="block">{t('scenarioForm.enabled')}</Label>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-1" />
              )}
            />
          </div>
        </div>
      </div>

      {/* ── Rules ────────────────────────────── */}
      <div className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('scenarioForm.ruleList')} ({ruleEntries.length})
        </h4>

        <div className="space-y-4">
          {ruleEntries.map((entry, idx) => (
            <div key={entry.id} className="rounded-md border bg-muted/10 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{t('scenarioForm.rule')} {idx + 1}</span>
                <div className="ml-auto flex gap-1">
                  <Button
                    type="button" variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => moveEntry(entry.id, 'up')}
                    disabled={idx === 0}
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button" variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => moveEntry(entry.id, 'down')}
                    disabled={idx === ruleEntries.length - 1}
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => removeEntry(entry.id)}
                    disabled={ruleEntries.length <= 1}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground">{t('scenarioForm.ifCondition')}</p>
                <RuleBuilder
                  node={entry.ruleNode}
                  onChange={(ruleNode) => updateEntry(entry.id, { ruleNode })}
                />
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground">{t('scenarioForm.thenResponse')}</p>
                <ResponseForm
                  value={entry.response}
                  onChange={(response) => updateEntry(entry.id, { response })}
                />
              </div>
            </div>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setRuleEntries(prev => [...prev, defaultEntry()])}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('scenarioForm.addRule')}
        </Button>
      </div>

      {/* ── Default response ──────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t('scenarioForm.defaultResponse')}
          </h4>
          {defaultResp === null ? (
            <Button
              type="button" variant="outline" size="sm" className="h-7 text-xs"
              onClick={() => setDefaultResp(defaultResponse())}
            >
              <Plus className="mr-1 h-3 w-3" />{t('scenarioForm.setDefault')}
            </Button>
          ) : (
            <Button
              type="button" variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground hover:text-destructive"
              onClick={() => setDefaultResp(null)}
            >
              {t('scenarioForm.removeDefault')}
            </Button>
          )}
        </div>
        {defaultResp !== null ? (
          <ResponseForm value={defaultResp} onChange={setDefaultResp} />
        ) : (
          <p className="text-xs text-muted-foreground">
            {t('scenarioForm.defaultNoMatch')}
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting || mutation.isPending}>
          {mutation.isPending ? t('common.creating') : t('common.create')}
        </Button>
      </div>
    </form>
  )
}
